import React from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  Sparkles, 
  Users, 
  TrendingUp, 
  Zap, 
  Mail, 
  Search, 
  Linkedin,
  Target,
  Award,
  Building,
  BarChart,
  Lightbulb,
  MessageSquare,
  CheckCircle,
  Calendar
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

export default function DiLuccia() {
  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Briefcase className="w-16 h-16 text-golden animate-pulse" />
              <Sparkles className="w-8 h-8 text-blue-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Di<span className="text-golden">Luccia</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Automated Lead Generation System
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              A comprehensive lead generation solution designed specifically for DiLuccia to connect with high-quality wholesale buyers and fashion retailers at scale, driving business growth and expanding your market presence.
            </p>
          </div>
        </motion.div>

        {/* Introduction Section */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">The Automated Lead Generation System</h2>
          <p className="text-gray-300 mb-6">
            Our system leverages cutting-edge automation to deliver massive personalized outreach campaigns, sending up to <span className="text-golden font-semibold">2,000 highly targeted emails daily</span> to fashion retailers and wholesale buyers who need your unique accessories and products.
          </p>
          <p className="text-gray-300 mb-6">
            What makes this approach different is the level of personalization. We don't just send emails to random contacts—we specifically reach out to buyers actively looking to expand their product lines, ensuring our outreach is relevant and timely.
          </p>
          <p className="text-gray-300">
            The system works 24/7, continuously generating leads while you focus on your core business activities. It's like having an entire sales team working around the clock, but at a fraction of the cost.
          </p>
        </MouseFollowEffect>

        {/* System Components */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-8 text-center">System Components</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Linkedin className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">LinkedIn Sales Navigator</h3>
                  <p className="text-gray-300">
                    We use LinkedIn Sales Navigator to identify and target the perfect prospects for DiLuccia. This powerful tool allows us to filter by industry, company size, job title, and more, ensuring we're reaching decision-makers in fashion retail.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Zap className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Make.com</h3>
                  <p className="text-gray-300">
                    Make.com serves as the automation backbone of our system, connecting all components and orchestrating the entire workflow. It handles everything from data processing to campaign management.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Mail className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Instantly.ai</h3>
                  <p className="text-gray-300">
                    Instantly.ai handles the email delivery with industry-leading deliverability rates. It manages email warming, sending schedules, and follow-ups automatically.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Search className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Any Mail Finder</h3>
                  <p className="text-gray-300">
                    This specialized tool helps us discover verified email addresses for your target prospects with exceptional accuracy. It ensures we're reaching the right buyers through their preferred channel.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* Personalization Example */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">The Power of Personalization</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Sample Outreach</h3>
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700 mb-4">
                  <p className="text-gray-300 text-sm italic">
                    Subject: Your Spring Collection + DiLuccia Accessories
                  </p>
                  <div className="border-l-2 border-golden pl-4 mt-4">
                    <p className="text-gray-300 text-sm mb-2">
                      Hi [Name],
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      Love how [Store Name]'s latest collection embraces sustainable fashion. Your commitment to ethical sourcing caught my eye, and I noticed you might be looking for unique accessories to complement your spring line.
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      DiLuccia's handcrafted pieces have helped retailers like yours increase average transaction value by 22% through strategic merchandising.
                    </p>
                    <p className="text-gray-300 text-sm">
                      Would you be open to a brief conversation about how our accessories could enhance your spring collection?
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 italic text-center">
                  "If you were a retail buyer, you'd actually reply to this email."
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Why This Works</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Personalized with specific details about their business</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>References their current collection and values</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Clear value proposition with specific metrics</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Relevant social proof from similar retailers</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Low-pressure call to action</span>
                  </li>
                </ul>
                <div className="mt-6 bg-gradient-to-r from-golden/20 to-black/30 p-5 rounded-xl border border-golden/30">
                  <h4 className="font-semibold mb-2">For DiLuccia</h4>
                  <p className="text-gray-300 text-sm">
                    We'll craft similar personalized messages that speak directly to the needs of fashion retailers and wholesale buyers, highlighting your unique designs and quality craftsmanship.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Case Studies */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Success Stories</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="mb-4">
                <img 
                  src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80"
                  alt="Retail Store"
                  className="w-full h-48 object-cover rounded-xl"
                />
              </div>
              <h3 className="text-xl font-bold mb-3 text-golden">Boutique Chain Success</h3>
              <p className="text-gray-300 mb-4">
                "The lead generation system has transformed our wholesale business. Within three months, we secured partnerships with 12 new boutiques, representing over $180,000 in annual recurring revenue. The personalized approach made all the difference."
              </p>
              <p className="text-golden font-semibold">
                — Premium Accessories Brand, 280% ROI in 90 days
              </p>
            </div>

            <div className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="mb-4">
                <img 
                  src="https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&q=80"
                  alt="Fashion Retail"
                  className="w-full h-48 object-cover rounded-xl"
                />
              </div>
              <h3 className="text-xl font-bold mb-3 text-golden">Department Store Breakthrough</h3>
              <p className="text-gray-300 mb-4">
                "The quality of leads we're getting is remarkable. These are retailers actively looking to expand their accessories line, and the personalized approach makes all the difference in getting responses from busy buyers."
              </p>
              <p className="text-golden font-semibold">
                — Luxury Accessories Brand, 15 New Retail Partners in 60 Days
              </p>
            </div>
          </div>
        </div>

        {/* Investment Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Your Investment</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">What's Included</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Complete lead generation system setup</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Custom targeting for fashion retail buyers</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Professional content creation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Automated follow-up sequences</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Real-time analytics dashboard</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Ongoing campaign optimization</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Weekly performance reports</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700 mb-6">
                  <h3 className="text-xl font-bold mb-2 text-center">Our Commitment</h3>
                  <div className="bg-golden/20 p-4 rounded-lg border border-golden/30 mb-4">
                    <p className="text-center font-bold text-white">
                      We Only Win When You Win
                    </p>
                    <p className="text-center text-sm text-gray-300">
                      Pay only for results, with a small setup fee
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">One-time setup fee:</span>
                      <span className="text-golden font-bold">$2,300</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Performance fee:</span>
                      <span className="text-golden font-bold">8% per successful deal</span>
                    </div>
                    <p className="text-center text-gray-300 text-sm mt-4">
                      No monthly retainer - you only pay when we deliver results
                    </p>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold mb-2 text-center">ROI Highlights</h3>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-golden">300%+</p>
                      <p className="text-sm text-gray-300">Potential ROI</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-golden">30 Days</p>
                      <p className="text-sm text-gray-300">Average Payback</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">Ready to Transform Your Lead Generation?</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Let's discuss how our lead generation system can work for DiLuccia and start delivering qualified leads within days.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/lead-gen-setup-call"
                  className="px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Your Strategy Call
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}